import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { authApi } from "../api"
import { getStoredToken, setStoredToken, setUnauthorizedHandler } from "../api/http"
import { forceDisconnectSharedSocket } from "../realtime/sharedSocket"

const AuthContext = createContext(null)

/**
 * Who is signed in, and what they may do.
 *
 * `user.grants` comes from the login response and is what the permission layer
 * reads. It is deliberately NOT refreshed on a timer: the server re-reads
 * permissions from the database on every request, and when a role or a permission
 * changes it revokes the affected sessions — so a stale client is corrected by
 * being signed out, not by polling. Polling would be a request per user per
 * interval to detect something that almost never happens.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(() => getStoredToken())
  const [loading, setLoading] = useState(Boolean(getStoredToken()))
  const [sessionEndedReason, setSessionEndedReason] = useState(null)

  const signOutLocally = useCallback((reason = null) => {
    setStoredToken(null)
    setToken(null)
    setUser(null)
    setSessionEndedReason(reason)
    // Immediately, not on the shared socket's grace delay: otherwise presence
    // shows the person as online for a second after they sign out, and the tab
    // keeps its subscription for that long too.
    forceDisconnectSharedSocket()
  }, [])

  // The http client calls this when the server says the session is gone —
  // expired, revoked, or ended by a role change on another device.
  useEffect(() => {
    setUnauthorizedHandler((code) => signOutLocally(code))
    return () => setUnauthorizedHandler(null)
  }, [signOutLocally])

  /*
   * Restore the session on load.
   *
   * A token in localStorage is a claim, not proof — it may have been revoked
   * while the tab was closed. `/auth/me` is what turns it back into a session,
   * and it also refreshes the grants, so a permission change that did not revoke
   * the session is still picked up on the next page load.
   */
  useEffect(() => {
    let cancelled = false
    const stored = getStoredToken()
    if (!stored) {
      setLoading(false)
      return undefined
    }

    authApi
      .me()
      .then((data) => {
        if (cancelled) return
        setUser({ ...data.user, grants: data.grants, permissions: data.permissions })
        setToken(stored)
      })
      .catch(() => {
        // The 401 interceptor has already cleared the token. Anything else
        // (the server is down) also lands here — showing the login screen is the
        // honest outcome either way.
        if (!cancelled) signOutLocally()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [signOutLocally])

  const login = useCallback(async (username, password) => {
    const data = await authApi.login(username, password)
    setStoredToken(data.token)
    setToken(data.token)
    setUser({ ...data.user, grants: data.grants, permissions: data.permissions })
    setSessionEndedReason(null)
    return data
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // The session may already be gone on the server. Clearing locally is the
      // point of the call, and it must happen either way.
    }
    signOutLocally()
  }, [signOutLocally])

  /** Merge a change into the signed-in user without a round trip (preferences, avatar). */
  const patchUser = useCallback((changes) => {
    setUser((current) => (current ? { ...current, ...changes } : current))
  }, [])

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      sessionEndedReason,
      isAuthenticated: Boolean(user && token),
      login,
      logout,
      patchUser,
    }),
    [user, token, loading, sessionEndedReason, login, logout, patchUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used inside an AuthProvider")
  return ctx
}
